import { useMemo, useState } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import { format } from 'date-fns';
import clsx from 'clsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import DateRangeCalendar, { type DateRangeValue } from '@/components/DateRangeCalendar';
import { dateFormat } from '@/utils/dateFormat';

type DatePreset = 'custom' | 'past7Days' | 'past30Days' | 'past90Days' | 'past12Months';

type Props = {
  from: string;
  to: string;
  onDateRangeChange: (from: string, to: string) => void;
};

const PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'custom', label: 'カスタム' },
  { value: 'past7Days', label: '過去 7日間' },
  { value: 'past30Days', label: '過去 30日間' },
  { value: 'past90Days', label: '過去 90日間' },
  { value: 'past12Months', label: '過去 12ヶ月間' },
];

/** 'YYYY-MM-DD' 文字列を Date に変換する。空文字は null を返す */
function parseDateString(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/** Date を 'YYYY-MM-DD' 文字列に整形する */
function formatDateParam(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** プリセットから期間を計算する */
function buildPresetRange(preset: Exclude<DatePreset, 'custom'>): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date(end);
  switch (preset) {
    case 'past7Days':
      start.setDate(end.getDate() - 6);
      break;
    case 'past30Days':
      start.setDate(end.getDate() - 29);
      break;
    case 'past90Days':
      start.setDate(end.getDate() - 89);
      break;
    case 'past12Months':
      start.setMonth(end.getMonth() - 12);
      break;
  }
  return { start, end };
}

/** 現在の期間がいずれかのプリセットに一致すればそのラベルを返す */
function getPresetLabel(range: DateRangeValue): string | null {
  if (!range.start || !range.end) return null;
  return (
    PRESET_OPTIONS.find((option) => {
      if (option.value === 'custom') return false;
      const presetRange = buildPresetRange(option.value);
      return (
        formatDateParam(presetRange.start) === formatDateParam(range.start as Date) &&
        formatDateParam(presetRange.end) === formatDateParam(range.end as Date)
      );
    })?.label ?? null
  );
}

export default function AnalysisDateRangePicker({ from, to, onDateRangeChange }: Props) {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('custom');
  const currentRange = useMemo<DateRangeValue>(
    () => ({ start: parseDateString(from), end: parseDateString(to) }),
    [from, to]
  );
  const [draftRange, setDraftRange] = useState<DateRangeValue>(currentRange);

  const hasSelection = Boolean(currentRange.start || currentRange.end);
  const displayText =
    getPresetLabel(currentRange) ??
    (hasSelection
      ? `${currentRange.start ? dateFormat(currentRange.start) : ''} ~ ${
          currentRange.end ? dateFormat(currentRange.end) : ''
        }`
      : '期間');

  const canSubmit = Boolean(draftRange.start && draftRange.end);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftRange(currentRange);
      setSelectedPreset('custom');
    }
    setOpen(nextOpen);
  };

  const handlePresetClick = (preset: DatePreset) => {
    setSelectedPreset(preset);
    setDraftRange(preset === 'custom' ? { start: null, end: null } : buildPresetRange(preset));
  };

  const handleCalendarChange = (range: DateRangeValue) => {
    setSelectedPreset('custom');
    setDraftRange(range);
  };

  const handleSubmit = () => {
    if (!draftRange.start || !draftRange.end) return;
    onDateRangeChange(formatDateParam(draftRange.start), formatDateParam(draftRange.end));
    setOpen(false);
  };

  // 期間をデフォルト（過去30日）に明示リセットする。
  // 空文字を渡すと analysisSearchSchema 側の .catch でデフォルト日付に置き換わるため、
  // UI 上「クリア」と動作が乖離する。明示的にデフォルトをセットして整合させる。
  const handleReset = () => {
    const range = buildPresetRange('past30Days');
    setSelectedPreset('past30Days');
    setDraftRange(range);
    onDateRangeChange(formatDateParam(range.start), formatDateParam(range.end));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="期間を選択"
          aria-haspopup="dialog"
          className={clsx(
            'flex h-[42px] min-w-[236px] items-center justify-between gap-2 rounded border border-solid border-border bg-white px-3 focus-visible:outline-hidden',
            open && 'border-2 border-primary'
          )}
        >
          <span
            className={clsx(
              'truncate text-sm leading-[1.7] whitespace-nowrap',
              hasSelection ? 'text-main' : 'text-placeholder'
            )}
          >
            {displayText}
          </span>
          <MdCalendarToday className="size-5 shrink-0 text-placeholder" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={6} className="w-auto rounded p-0">
        <div className="flex overflow-hidden rounded">
          {/* 左ペイン: プリセット */}
          <div className="w-[160px] border-r border-solid border-border bg-white py-2">
            {PRESET_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={clsx(
                  'flex h-[36px] w-full items-center border-0 px-3 text-left text-sm',
                  selectedPreset === option.value
                    ? 'bg-primary_lighten font-bold text-primary'
                    : 'bg-white text-main hover:bg-gray-50'
                )}
                onClick={() => handlePresetClick(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* 右ペイン: カレンダー */}
          <div className="bg-white p-3">
            <DateRangeCalendar value={draftRange} onChange={handleCalendarChange} />
          </div>
        </div>

        {/* フッター */}
        <div className="flex items-center justify-end gap-2 border-t border-solid border-border px-3 py-2">
          <button
            type="button"
            className="h-8 rounded border border-solid border-border bg-white px-3 text-xs font-bold text-main hover:bg-gray-50"
            onClick={handleReset}
          >
            リセット
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            className="h-8 rounded border-0 bg-primary px-3 text-xs font-bold text-white hover:bg-primary_darken disabled:cursor-not-allowed disabled:opacity-40"
            onClick={handleSubmit}
          >
            決定
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
